/**
 * The AI provider accounts agents run on (S-21, ADR-131).
 *
 * The screen that gives a name to something the platform used to decide by
 * accident. Before it, an agent's account was whichever sign-in on the machine
 * was newest — so two agents could not deliberately use two accounts of the
 * same provider, signing in anywhere silently moved every agent, and "which
 * account did this work" had no answer at all.
 *
 * Four refusals shape it, and each is a thing this screen will not do:
 *
 *  - **No key is ever shown.** The API cannot return one, so there is nothing
 *    to reveal and no "show" button to argue about. Rotating means typing a new
 *    one; renaming does not ask for the old one back.
 *  - **No gauge.** Neither Claude nor Codex reports how much of a subscription
 *    is left, so there is no percentage to draw and drawing one would be
 *    invention. Usage is tokens actually reported, or the word «unknown».
 *  - **Nothing measured is not zero.** A connection nothing has run on says so
 *    in words. A «0 tokens» row would read as "this account is idle" when it
 *    means "nobody wrote anything down".
 *  - **Status is a sentence, not a light that acts.** Verifying records what
 *    was seen once. It raises no alert, and nothing anywhere fails over to a
 *    second account — that is what would make the attribution above a lie again.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import type { ManagedLoginView, ProviderConnectionView, ProviderLoginSessionView } from '../../client/types.js';
import { useConsoleMutation, useHarnesses, useManagedLogins, useProviderConnections } from '../queries.js';
import { QueryPhase, Section } from '../components/state.js';
import { useStore } from '../store-context.js';
import { Button } from '@primer/react';

/** The one string that means "the machine the control plane runs on". */
const THIS_MACHINE = '';

export function ConnectionsScreen(): ReactNode {
  const store = useStore();
  const queryClient = useQueryClient();
  const connections = useProviderConnections();
  const logins = useManagedLogins();
  const harnesses = useHarnesses();

  const create = useConsoleMutation<
    {
      name: string;
      provider: string;
      kind: 'subscription' | 'api';
      environment: string;
      loginPath?: string;
      secretValue?: string;
      description?: string;
    },
    unknown
  >((data, context, input) => data.createProviderConnection(input, context), [['provider-connections']]);

  const verify = useConsoleMutation<string, unknown>(
    (data, context, id) => data.verifyProviderConnection(id, context),
    [['provider-connections'], ['managed-logins']],
  );

  const remove = useConsoleMutation<string, unknown>(
    (data, context, id) => data.removeProviderConnection(id, context),
    [['provider-connections']],
  );

  const rotate = useConsoleMutation<{ id: string; secretValue: string }, unknown>(
    (data, context, input) => data.updateProviderConnection(input.id, { secretValue: input.secretValue }, context),
    [['provider-connections']],
  );

  const startLogin = useConsoleMutation<'claude' | 'codex' | 'grok', ProviderLoginSessionView>(
    (data, context, selected) => data.startProviderLogin(selected, context),
    [],
  );
  const sendLoginInput = useConsoleMutation<{ id: string; input: string }, ProviderLoginSessionView>(
    (data, context, value) => data.writeProviderLogin(value.id, value.input, context),
    [],
  );
  const cancelLogin = useConsoleMutation<string, unknown>(
    (data, context, id) => data.cancelProviderLogin(id, context),
    [],
  );

  const [provider, setProvider] = useState('claude');
  const [kind, setKind] = useState<'subscription' | 'api'>('subscription');
  const [name, setName] = useState('');
  const [loginPath, setLoginPath] = useState('');
  const [environment, setEnvironment] = useState(THIS_MACHINE);
  const [secret, setSecret] = useState('');
  const [description, setDescription] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [loginSessionId, setLoginSessionId] = useState<string | null>(null);
  const [loginInput, setLoginInput] = useState('');

  const loginSession = useQuery({
    queryKey: ['provider-login', loginSessionId],
    queryFn: () => store.data.providerLoginStatus(loginSessionId ?? ''),
    enabled: loginSessionId !== null,
    refetchInterval: (query) => ['running', 'cancelling'].includes(query.state.data?.state ?? '') ? 1_000 : false,
  });

  useEffect(() => {
    if (loginSession.data?.state !== 'succeeded') return;
    void queryClient.invalidateQueries({ queryKey: ['managed-logins'] });
  }, [loginSession.data?.state, queryClient]);

  if (connections.isPending || connections.error !== null) {
    return (
      <QueryPhase
        isPending={connections.isPending}
        error={connections.error}
        source="AI provider connections"
        rows={4}
      />
    );
  }

  const saved = connections.data ?? [];
  const offered = (logins.data ?? []).filter((login) => login.harness === provider);
  const providers = (harnesses.data ?? []).map((harness) => harness.id);

  const onAdd = (): void => {
    setFailure(null);
    create.mutate(
      {
        name: name.trim(),
        provider,
        kind,
        environment: environment.trim(),
        ...(kind === 'subscription' ? { loginPath: loginPath.trim() } : { secretValue: secret }),
        ...(description.trim().length === 0 ? {} : { description: description.trim() }),
      },
      {
        onSuccess: () => {
          // The key is cleared at once and deliberately: it is the only copy in
          // this browser's memory and there is no reason for it to sit in a
          // form field behind whatever the operator does next.
          setName('');
          setSecret('');
          setLoginPath('');
          setDescription('');
          setStep(1);
          setLoginSessionId(null);
        },
        onError: (error) => setFailure(String(error)),
      },
    );
  };

  const ready =
    name.trim().length > 0 && (kind === 'subscription' ? loginPath.trim().length > 0 : secret.length > 0);

  return (
    <div className="stack">
      <Section
        label="Add an AI account"
        lede="A short setup wizard. Each login gets its own name, so you can tell accounts apart and choose one for every agent."
      >
        <div className="provider-wizard-steps" aria-label={`Step ${String(step)} of 3`}>
          {['Choose service', 'Sign in & name', 'Confirm'].map((label, index) => (
            <span key={label} className={step >= index + 1 ? 'is-active' : ''}><b>{index + 1}</b>{label}</span>
          ))}
        </div>

        {step === 1 && (
          <div className="provider-wizard-pane">
            <div className="provider-choice-grid">
              {(providers.length === 0 ? ['claude', 'codex', 'kimi'] : providers).map((id) => (
                <button key={id} type="button" className={`provider-choice ${provider === id ? 'is-selected' : ''}`} onClick={() => { setProvider(id); if (id !== 'claude' && id !== 'codex' && id !== 'grok') setKind('api'); setLoginSessionId(null); setLoginPath(''); }}>
                  <strong>{providerLabel(id)}</strong><span>{id === 'claude' ? 'Claude subscription or Anthropic key' : id === 'codex' ? 'ChatGPT/Codex subscription or OpenAI key' : id === 'grok' ? 'SuperGrok subscription or xAI key' : 'API key'}</span>
                </button>
              ))}
            </div>
            <div className="provider-auth-choice">
              {(provider === 'claude' || provider === 'codex' || provider === 'grok') && (
                <label><input type="radio" checked={kind === 'subscription'} onChange={() => setKind('subscription')} /> Sign in with my subscription</label>
              )}
              <label><input type="radio" checked={kind === 'api'} onChange={() => setKind('api')} /> Use an API key</label>
            </div>
            <Button variant="primary" onClick={() => setStep(2)}>Continue</Button>
          </div>
        )}

        {step === 2 && (
          <div className="provider-wizard-pane">
            {kind === 'subscription' ? (
              <>
                <div className="provider-login-box">
                  <strong>Sign in to {providerLabel(provider)}</strong>
                  <p className="editor-hint">A secure provider page will identify the account. Use an incognito window when adding a second account.</p>
                  {loginSessionId === null && (
                    <Button variant="primary" disabled={startLogin.isPending} onClick={() => startLogin.mutate(provider as 'claude' | 'codex' | 'grok', { onSuccess: (session) => setLoginSessionId(session.id), onError: (error) => setFailure(String(error)) })}>
                      {startLogin.isPending ? 'Starting sign-in…' : `Sign in with ${providerLabel(provider)}`}
                    </Button>
                  )}
                  {loginSession.data !== undefined && (
                    <div className="provider-login-progress">
                      <p><strong>{loginStateLabel(loginSession.data.state)}</strong></p>
                      {extractLoginUrl(loginSession.data.output) !== null && <a className="provider-login-link" href={extractLoginUrl(loginSession.data.output) ?? '#'} target="_blank" rel="noreferrer">Open secure sign-in page ↗</a>}
                      <pre>{loginSession.data.output || 'Waiting for the provider…'}</pre>
                      {loginSession.data.state === 'running' && (
                        <><div className="provider-login-input"><input value={loginInput} placeholder="Paste a confirmation code if the provider asks for one" onChange={(event) => setLoginInput(event.target.value)} /><Button disabled={loginInput.trim().length === 0 || sendLoginInput.isPending} onClick={() => sendLoginInput.mutate({ id: loginSession.data.id, input: loginInput }, { onSuccess: () => setLoginInput('') })}>Submit code</Button></div><Button disabled={cancelLogin.isPending} onClick={() => cancelLogin.mutate(loginSession.data.id, { onSuccess: () => void loginSession.refetch() })}>Cancel sign-in</Button></>
                      )}
                      {loginSession.data.state !== 'running' && loginSession.data.state !== 'succeeded' && <Button onClick={() => setLoginSessionId(null)}>Try again</Button>}
                    </div>
                  )}
                </div>
                <fieldset className="provider-account-list">
                  <legend>Choose the account to add</legend>
                  {offered.filter((login) => login.signedIn).length === 0 ? <p className="editor-hint">Complete sign-in above. Your account will appear here automatically.</p> : offered.filter((login) => login.signedIn).map((login: ManagedLoginView) => (
                    <label key={login.path}><input type="radio" name="managed-login" checked={loginPath === login.path} onChange={() => { setLoginPath(login.path); if (name.trim().length === 0) setName(suggestConnectionName(provider, login.label)); }} /><span><strong>{login.label}</strong><small>{providerLabel(provider)} subscription</small></span></label>
                  ))}
                </fieldset>
                <details className="provider-advanced">
                  <summary>Advanced: use an existing login on another machine</summary>
                  <label className="field"><span className="field-label">Signed-in folder</span><input value={loginPath} placeholder="/path/to/the/provider/home" onChange={(event) => setLoginPath(event.target.value)} /><span className="editor-hint">Normally the wizard fills this in. Enter it only for a login created outside this screen.</span></label>
                  <label className="field"><span className="field-label">Machine</span><input value={environment} placeholder="This control-plane machine" onChange={(event) => setEnvironment(event.target.value)} /><span className="editor-hint">Leave empty for this machine, or enter the exact execution-environment name that owns the folder.</span></label>
                </details>
              </>
            ) : (
              <label className="field"><span className="field-label">{providerLabel(provider)} API key</span><textarea rows={3} value={secret} placeholder="Paste the API key" onChange={(event) => setSecret(event.target.value)} autoComplete="off" spellCheck={false} /><span className="editor-hint">The key is encrypted in transit, stored on this installation, and is never shown again.</span></label>
            )}
            <label className="field"><span className="field-label">Account name</span><input value={name} placeholder={`${provider}-work`} onChange={(event) => setName(event.target.value)} /><span className="editor-hint">Use something recognizable, for example “Codex — work@company.com”. Agents will use this name in their settings.</span></label>
            <label className="field"><span className="field-label">Note (optional)</span><input value={description} placeholder="Team subscription" onChange={(event) => setDescription(event.target.value)} /></label>
            <div className="preset-row"><Button onClick={() => setStep(1)}>Back</Button><Button variant="primary" disabled={!ready} onClick={() => setStep(3)}>Review</Button></div>
          </div>
        )}

        {step === 3 && (
          <div className="provider-wizard-pane provider-review-card">
            <h3>{name}</h3><p>{providerLabel(provider)} · {kind === 'subscription' ? 'Subscription login' : 'API key'}</p>
            {kind === 'subscription' && <p className="editor-hint">Signed in as {offered.find((login) => login.path === loginPath)?.label ?? name}</p>}
            <p className="editor-hint">After adding it, choose this account in Settings → Roles & team for each agent that should use it.</p>
            <div className="preset-row"><Button onClick={() => setStep(2)}>Back</Button><Button variant="primary" disabled={!ready || create.isPending} onClick={onAdd}>{create.isPending ? 'Adding…' : 'Add account'}</Button></div>
          </div>
        )}
        {failure !== null && <p className="placeholder-inline">{failure}</p>}
      </Section>

      <Section
        label="Connected accounts"
        lede="What each account is, what it has actually spent, and who is using it."
      >
        {saved.length === 0 ? (
          <p className="placeholder-inline">
            No accounts are connected yet. Until one is, every agent runs on whatever sign-in its machine happens to
            have — which is why "who spent this" has no answer.
          </p>
        ) : (
          <div className="editor-list provider-connection-list">
            {saved.map((connection) => (
              <ConnectionCard
                key={connection.id}
                connection={connection}
                managedLogin={connection.environment === ''
                  ? (logins.data ?? []).find((login) => login.harness === connection.provider && login.path === connection.loginPath) ?? null
                  : null}
                busy={verify.isPending || remove.isPending || rotate.isPending}
                onVerify={() => verify.mutate(connection.id)}
                onRemove={() => remove.mutate(connection.id)}
                onRotate={(value) => rotate.mutate({ id: connection.id, secretValue: value })}
                error={
                  remove.isError ? String(remove.error).slice(0, 200) : rotate.isError ? String(rotate.error).slice(0, 200) : null
                }
              />
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function providerLabel(id: string): string {
  if (id === 'claude') return 'Claude';
  if (id === 'codex') return 'Codex';
  if (id === 'kimi') return 'Kimi';
  if (id === 'grok') return 'Grok Build';
  return id;
}

function suggestConnectionName(provider: string, account: string): string {
  const identity = account.split('@')[0]?.replace(/[^a-z0-9]+/giu, '-').replace(/^-|-$/gu, '').toLowerCase() || 'account';
  return `${provider}-${identity}`;
}

function extractLoginUrl(output: string): string | null {
  return output.match(/https:\/\/[^\s<>"']+/u)?.[0] ?? null;
}

function loginStateLabel(state: ProviderLoginSessionView['state']): string {
  if (state === 'running') return 'Waiting for sign-in…';
  if (state === 'cancelling') return 'Stopping sign-in…';
  if (state === 'succeeded') return 'Signed in successfully';
  if (state === 'cancelled') return 'Sign-in cancelled';
  return 'Sign-in failed';
}

function ConnectionCard({
  connection,
  managedLogin,
  busy,
  onVerify,
  onRemove,
  onRotate,
  error,
}: {
  connection: ProviderConnectionView;
  managedLogin: ManagedLoginView | null;
  busy: boolean;
  onVerify: () => void;
  onRemove: () => void;
  onRotate: (value: string) => void;
  error: string | null;
}): ReactNode {
  const [replacement, setReplacement] = useState('');

  /*
   * Disconnect is disabled with the reason attached rather than offered and
   * then refused. The server refuses either way — the check runs inside the
   * deleting transaction, because a count read beforehand is stale by the time
   * the row is gone — but a button that explains itself beats a red box.
   */
  const blocked =
    connection.busy
      ? 'an agent is working on it right now, and disconnecting would cut that run off mid-task'
      : connection.assignments.length > 0
        ? `still the account for ${connection.assignments.map((assignment) => assignment.agentId).join(', ')}`
        : null;

  return (
    <article className="editor-card provider-connection-card">
      <header className="provider-connection-head">
        <div className="provider-connection-identity">
          <strong className="editor-title-static">{connection.name}</strong>
          <span className="editor-hint">
            {connection.provider} · {connection.kind === 'api' ? 'API key' : 'signed-in subscription'} ·{' '}
            {connection.environment === '' ? 'this machine' : connection.environment}
          </span>
          {managedLogin !== null && <span className="provider-account-email">{managedLogin.label}</span>}
        </div>
        <div className="preset-row">
          <Button disabled={busy} onClick={onVerify}>
            Check
          </Button>
          <Button disabled={busy || blocked !== null} onClick={onRemove}>
            Disconnect
          </Button>
        </div>
      </header>

      <div className="provider-connection-meta">
      <p className={connection.status.state === 'unusable' ? 'repo-probe repo-probe-bad' : 'editor-hint'}>
        <strong>{statusWord(connection.status.state)}</strong>
        {connection.status.detail === null ? '' : ` — ${connection.status.detail}`}
        {connection.status.observedAt === null
          ? ' (never checked)'
          : ` (checked ${new Date(connection.status.observedAt).toLocaleString([], {
              day: 'numeric',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
            })})`}
      </p>
      <p className="editor-hint">
        {connection.assignments.length === 0
          ? 'No agent uses this account yet.'
          : `Used by ${connection.assignments
              .map((assignment) => `${assignment.agentId} (${assignment.teamName})`)
              .join(', ')}.`}
      </p>
      </div>

      {managedLogin !== null && <ProviderLimits login={managedLogin} />}

      {connection.usage.attempts > 0 && <p className="editor-hint provider-historic-usage">{usageSentence(connection)}</p>}

      {(connection.kind === 'subscription' && connection.loginPath !== null || connection.description !== null) && (
        <details className="provider-card-technical"><summary>Technical details</summary>
          {connection.description !== null && <span>{connection.description}</span>}
          {connection.loginPath !== null && <code>{connection.loginPath}</code>}
        </details>
      )}

      {blocked !== null && <p className="editor-hint">Cannot disconnect: {blocked}.</p>}

      {connection.kind === 'api' && (
        <div className="credential-value">
          <textarea
            rows={2}
            value={replacement}
            placeholder="Paste a new key to replace the stored one"
            onChange={(event) => setReplacement(event.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
          <div className="preset-row">
            <Button
              variant="primary"
              disabled={busy || replacement.trim().length === 0}
              onClick={() => {
                onRotate(replacement);
                setReplacement('');
              }}
            >
              Replace key
            </Button>
          </div>
        </div>
      )}

      {error !== null && <p className="repo-probe repo-probe-bad">{error}</p>}
    </article>
  );
}

function ProviderLimits({ login }: { login: ManagedLoginView }): ReactNode {
  if (login.limits.length === 0) {
    return <p className="provider-limits-empty">Usage remaining: not reported for this account.</p>;
  }
  return (
    <div className="provider-limit-grid" aria-label="Subscription usage remaining">
      {login.limits.map((limit) => {
        const remaining = limit.usedPercent === null ? null : Math.max(0, 100 - limit.usedPercent);
        return <div className="provider-limit" key={limit.label}>
          <span>{limit.label}</span>
          <strong>{remaining === null ? `${limit.available ?? 0} available` : `${remaining}% left`}</strong>
          {remaining !== null && <progress max={100} value={remaining} aria-label={`${limit.label}: ${remaining}% remaining`} />}
          {limit.resetsAt !== null && <small>Resets {new Date(limit.resetsAt).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</small>}
        </div>;
      })}
    </div>
  );
}

function statusWord(state: ProviderConnectionView['status']['state']): string {
  if (state === 'ready') return 'Ready';
  if (state === 'unusable') return 'Not usable';
  return 'Unknown';
}

/**
 * Usage in words, including the case where there is none.
 *
 * Historic consumption is separate from the provider's live quota windows.
 */
function usageSentence(connection: ProviderConnectionView): string {
  const { usage } = connection;
  if (usage.attempts === 0) {
    return 'Spend: unknown — nothing has been measured on this account yet.';
  }
  const tokens = `${usage.inputTokens.toLocaleString()} in / ${usage.outputTokens.toLocaleString()} out`;
  const since = usage.since === null ? '' : ` since ${new Date(usage.since).toLocaleDateString()}`;
  const cost =
    usage.costMicros === 0
      ? 'cost unknown'
      : `about $${(usage.costMicros / 1_000_000).toFixed(2)} (${usage.quality})`;
  return `Measured here: ${tokens} across ${usage.attempts.toLocaleString()} attempts${since} · ${cost}.`;
}
